import type { Document } from '../hooks/useDocuments'
import type { Source } from '../hooks/useChat'

const API_BASE = import.meta.env.VITE_API_URL || 'https://dominus-api.hamed-barkh.workers.dev/api'

// Voice profile types
export interface VoiceProfileStatus {
  hasVoiceAccess: boolean
  phoneNumbers: string[]
  createdAt?: string
  updatedAt?: string
  isLocked?: boolean
}

export interface VoiceProfileResponse {
  success: boolean
  phoneNumber?: string
  message: string
}

// Gmail types
export interface GmailSyncState {
  known_message_ids: string[]
  last_sync_at: string
  email_count: number
  syncing: boolean
  range: string
  total_batches?: number
  completed_batches?: number
  last_error?: string
}

export interface GmailStatus {
  connected: boolean
  syncState: GmailSyncState | null
}

interface ChatRequest {
  message: string
  mentionedDocuments?: string[]
  mentionedContacts?: Array<{ name: string; email: string }>
  sessionId?: string | null
  history?: Array<{ role: 'user' | 'assistant'; content: string }>
}

interface WebSource {
  url: string
  title: string
}

interface ChatResponse {
  message: string
  sources?: Source[]
  web_sources?: WebSource[]
  sessionId: string
}

async function fetchWithAuth(
  url: string,
  token: string | null,
  options: RequestInit = {}
): Promise<Response> {
  const headers: HeadersInit = {
    ...options.headers,
  }

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || `HTTP ${response.status}`)
  }

  return response
}

export const api = {
  async getDocuments(token: string | null): Promise<Document[]> {
    const response = await fetchWithAuth('/documents', token)
    return response.json()
  },

  async uploadDocument(
    file: File,
    token: string | null,
    onProgress?: (progress: number) => void
  ): Promise<Document> {
    const formData = new FormData()
    formData.append('file', file)

    // For progress tracking, we'd need XMLHttpRequest
    // For simplicity, we'll use fetch without progress
    const response = await fetchWithAuth('/documents', token, {
      method: 'POST',
      body: formData,
    })

    onProgress?.(100)
    return response.json()
  },

  async deleteDocument(documentId: string, token: string | null): Promise<void> {
    await fetchWithAuth(`/documents/${documentId}`, token, {
      method: 'DELETE',
    })
  },

  async chat(request: ChatRequest, token: string | null): Promise<ChatResponse> {
    const response = await fetchWithAuth('/chat', token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    return response.json()
  },

  // Voice profile methods
  async getVoiceProfile(token: string | null): Promise<VoiceProfileStatus> {
    const response = await fetchWithAuth('/voice/profile', token)
    return response.json()
  },

  async setVoiceProfile(
    phoneNumber: string,
    pin: string,
    token: string | null
  ): Promise<VoiceProfileResponse> {
    const response = await fetchWithAuth('/voice/profile', token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ phoneNumber, pin }),
    })
    return response.json()
  },

  async deleteVoiceProfile(token: string | null): Promise<VoiceProfileResponse> {
    const response = await fetchWithAuth('/voice/profile', token, {
      method: 'DELETE',
    })
    return response.json()
  },

  async unlockVoiceProfile(token: string | null): Promise<VoiceProfileResponse> {
    const response = await fetchWithAuth('/voice/profile/unlock', token, {
      method: 'POST',
    })
    return response.json()
  },

  // Gmail methods
  async getGmailStatus(token: string | null): Promise<GmailStatus> {
    const response = await fetchWithAuth('/gmail/status', token)
    return response.json()
  },

  async startGmailAuth(token: string | null): Promise<{ authUrl: string }> {
    const response = await fetchWithAuth('/gmail/auth', token)
    return response.json()
  },

  async syncGmail(range: string, token: string | null): Promise<{ status: string; newEmails?: number }> {
    const response = await fetchWithAuth('/gmail/sync', token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ range }),
    })
    return response.json()
  },

  async disconnectGmail(token: string | null): Promise<{ success: boolean }> {
    const response = await fetchWithAuth('/gmail/disconnect', token, {
      method: 'POST',
    })
    return response.json()
  },

  async searchContacts(query: string, token: string | null): Promise<Array<{ name: string; email: string }>> {
    const response = await fetchWithAuth(`/gmail/contacts/search?q=${encodeURIComponent(query)}`, token)
    return response.json()
  },
}
